import { oc } from '@orpc/contract';
import { z } from 'zod';
import { paginatedSchema, paginationQuerySchema } from './pagination';

/**
 * IT Operation Core: デバイス棚卸しの束ね（タグ）単位・Instance 単位の API（読み取り 5 本 + 書き込み 1 本）。
 *
 * Backend の正:
 * - `list-device-inventory-instances-by-tag.api-contract.ts`
 * - `count-device-inventory-instances-by-tag-tabs.api-contract.ts`
 * - `get-device-inventory-progress-by-tag.api-contract.ts`
 * - `list-device-inventory-self-reports-by-tag.api-contract.ts`
 * - `list-device-inventory-self-reports-by-package-instances.api-contract.ts`
 * - `bulk-complete-device-inventory-tasks-by-tag.api-contract.ts`（書き込み）
 *
 * `@standandforce/api-contract` は develop への push でのみ publish されるため、
 * 取り込まれるまでの間だけローカルに写している。
 * **取り込まれたらパッケージ export へ差し替え、本ファイルを削除する。**
 *
 * BE との意図的な差分は 1 点だけ:
 * BE が `z.date()` の項目を **すべて `z.string()`（ISO 文字列）で写している**。
 * OpenAPILink は JSON transport なので Date は ISO 文字列として届き、oRPC の client は
 * output を schema で検証しない（`JsonifiedClient<T>` が示すとおり `Date` → `string`）。
 * `z.date()` をそのまま写すと型だけ `Date` になって実体と食い違うため、wire に合わせる。
 * 既存ローカル contract の日付慣習（`contracts/accesses-ai.ts`）と同じ判断。
 */

/** @see prisma OperationStepStatus（ステップインスタンスの状態） */
const operationStepStatusValues = ['NotStarted', 'InProgress', 'Completed', 'Abort', 'Skipped', 'ExceptionHandled'] as const;

/** @see prisma OperationTaskStatus（タスクインスタンスの状態） */
const operationTaskStatusValues = ['NotStarted', 'InProgress', 'Completed', 'WaitingApproval', 'Abort', 'Skipped'] as const;

/**
 * @see prisma DeviceOperationTaskProgressStatus（デバイス単位の進捗）
 *
 * ⚠️ **v1 の語彙**。v2 の棚卸しは `DeviceOperationTask` を読み書きしないため、
 * 束ね軸 / Instance 軸のセルフレポート一覧（`DeviceInventorySelfReportSchema`）には
 * この値は載らない。旧 REST を使う v1 画面（`_root/packages/.../DeviceInventoryOperation`）
 * のためだけに残している。
 */
const deviceOperationTaskProgressStatusValues = ['NotStarted', 'InProgress', 'Completed', 'Aborted'] as const;

/**
 * @see prisma DeviceInventoryReportResult（セルフレポートの申告結果）
 *
 * ⚠️ BE の写し。prisma の enum に値を足したら両方に足すこと。**出力スキーマなので、
 * 漏れると一覧 API が丸ごと 500（Output validation failed）になる**。
 */
const deviceInventoryReportResultValues = ['OWNED', 'LOST', 'NOT_KNOWN', 'RETURNED', 'OTHER'] as const;

/** @see prisma DeviceInventoryCondition（デバイスの状態） */
const deviceInventoryConditionValues = ['GOOD', 'BAD'] as const;

/** @see prisma DeviceInventoryVerificationMethod（確認方法） */
const deviceInventoryVerificationMethodValues = ['PHYSICAL_CHECK', 'SCAN', 'PHOTO_RECORD', 'ASSET_TAG', 'INFORMAL', 'REPRESENTATIVE_BY_STAFF'] as const;

/** @see shared-kernel/domain/value/device-spec/device-genre.enum.ts */
const deviceGenreValues = ['WORKSTATION', 'LAPTOP', 'MOBILE', 'TABLET', 'MONITOR', 'PRINTER', 'SERVER', 'OTHER'] as const;

/** @see it-force/domain/service/device-inventory/device-inventory-display-status.resolver.ts */
const deviceInventoryDisplayStatusValues = ['REQUEST_BEFORE', 'REQUEST_IN_PROGRESS', 'SELF_REPORT_BEFORE', 'SELF_REPORT_IN_PROGRESS', 'REVIEW_BEFORE', 'REVIEW_IN_PROGRESS', 'REVIEW_COMPLETED'] as const;

/**
 * デバイス棚卸しパッケージのステップ名。
 * BE 側も domain を import できないため定数を手で写している（⚠️ 既存データと突き合わせるため変更不可）。
 */
const deviceInventoryStepNameValues = ['セルフレポートの依頼', 'セルフレポート', 'セルフレポート結果'] as const;
export const DEVICE_INVENTORY_GENRES = deviceGenreValues;

/**
 * 「所持確認」の選択肢。**並び順は BE の Dictionary の定義順**
 * （`attribute-selection.ts` の `VERIFICATION_STATUS_*`）で、
 * 「所持 → 未所持の理由」の順を崩さないこと。回答フォームの並びと揃える。
 *
 * ⚠️ この並びは `deviceInventoryReportResultValues`
 * （`OWNED, LOST, NOT_KNOWN, RETURNED, OTHER`）とは**意図的に違う**。
 * 前者は表示順、後者は BE の prisma enum の写しなので揃えない。
 */
export const DEVICE_INVENTORY_REPORT_RESULTS = ['OWNED', 'NOT_KNOWN', 'RETURNED', 'LOST', 'OTHER'] as const satisfies readonly DeviceInventoryReportResult[];
export type DeviceInventoryGenre = (typeof deviceGenreValues)[number];
export type DeviceInventoryReportProgressStatus = (typeof deviceOperationTaskProgressStatusValues)[number];
export type DeviceInventoryDisplayStatus = (typeof deviceInventoryDisplayStatusValues)[number];
export type DeviceInventoryReportResult = (typeof deviceInventoryReportResultValues)[number];
const DeviceGenreSchema = z.enum(deviceGenreValues);
const DeviceInventoryDisplayStatusSchema = z.enum(deviceInventoryDisplayStatusValues);
const DeviceInventoryReportResultSchema = z.enum(deviceInventoryReportResultValues);

// --- 1. 束ね内のインスタンス明細 + taskProgress ---

const DeviceInventoryInstanceOperatorSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** アバター未設定でも null ではなく空文字が入る（BE 既存 DTO の挙動） */
  avatarUrl: z.string()
});
const DeviceInventoryInstanceTaskSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.enum(operationTaskStatusValues),
  sortOrder: z.number()
});
const DeviceInventoryInstanceStepSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /** ステップの期日。未設定の場合は作成日時にフォールバックする */
  anchorDate: z.string(),
  status: z.enum(operationStepStatusValues),
  /** パターン定義上のステップ順（0 始まり） */
  sortOrder: z.number(),
  tasks: z.array(DeviceInventoryInstanceTaskSchema),
  createdAt: z.string(),
  updatedAt: z.string()
});
const DeviceInventoryInstanceDetailSchema = z.object({
  instanceId: z.string(),
  anchorDate: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** OperationPackageStatus の生値。解決できない場合は 'Unknown' */
  status: z.string(),
  createdByPerson: DeviceInventoryInstanceOperatorSchema.nullable(),
  steps: z.array(DeviceInventoryInstanceStepSchema)
});

/** 束ね全体のセルフレポート（Step2）タスクの進捗集計 */
const DeviceInventoryTaskProgressSchema = z.object({
  total: z.number(),
  completed: z.number(),
  inProgress: z.number(),
  /** total - completed - inProgress */
  pending: z.number(),
  /** completed / total の百分率（整数丸め）。total が 0 のときは 0 */
  percentage: z.number()
});
const ListDeviceInventoryInstancesByTagInputSchema = z.object({
  tagId: z.string()
});
const ListDeviceInventoryInstancesByTagOutputSchema = z.object({
  instanceDetails: z.array(DeviceInventoryInstanceDetailSchema),
  taskProgress: DeviceInventoryTaskProgressSchema
});
export const listDeviceInventoryInstancesByTagContract = oc.route({
  method: 'GET',
  path: '/v2/boe/packages/IT_OPERATION_CORE/instance-tags/{tagId}/device-inventory-instances'
}).input(ListDeviceInventoryInstancesByTagInputSchema).output(ListDeviceInventoryInstancesByTagOutputSchema);
export type ListDeviceInventoryInstancesByTagOutput = z.infer<typeof ListDeviceInventoryInstancesByTagOutputSchema>;

// --- 2. 6 タブの件数 ---

const CountDeviceInventoryInstancesByTagTabsInputSchema = z.object({
  tagId: z.string()
});

/**
 * 束ね画面のタブ件数。5 タブ（all を除く）は相互排他で、合計は必ず all と一致する。
 *
 * ⚠️ 本 API は `tagId` しか受け取らない。検索語・絞り込みを反映しないため、
 * 一覧に絞り込みを掛けている状態では件数と表示行数は一致しない（束ね全体の内訳を示す）。
 */
const CountDeviceInventoryInstancesByTagTabsOutputSchema = z.object({
  /** Step1（依頼）未完了 */
  notRequested: z.number(),
  /** Step1 完了 かつ Step2（回答）未完了（差戻しを含む） */
  notAnswered: z.number(),
  /** Step2 完了 かつ 対応不要 かつ Instance 未完了 */
  noActionRequired: z.number(),
  /** Step2 完了 かつ 要対応 かつ Instance 未完了 */
  actionRequired: z.number(),
  /** Instance（OperationPackageInstance）が Completed */
  completed: z.number(),
  /** 全件 */
  all: z.number()
});
export const countDeviceInventoryInstancesByTagTabsContract = oc.route({
  method: 'GET',
  path: '/v2/boe/packages/IT_OPERATION_CORE/instance-tags/{tagId}/device-inventory-instances/tab-counts'
}).input(CountDeviceInventoryInstancesByTagTabsInputSchema).output(CountDeviceInventoryInstancesByTagTabsOutputSchema);
export type CountDeviceInventoryInstancesByTagTabsOutput = z.infer<typeof CountDeviceInventoryInstancesByTagTabsOutputSchema>;

// --- 3. 進捗ステップ ---

const GetDeviceInventoryProgressByTagInputSchema = z.object({
  tagId: z.string()
});
const GetDeviceInventoryProgressByTagOutputSchema = z.object({
  /**
   * 束ね全体が今どのステップにいるか。
   * 束ね内の 1 件でもセルフレポートタスクが InProgress / Completed なら
   * 「セルフレポート」、そうでなければ「セルフレポートの依頼」。
   */
  progressStep: z.enum(deviceInventoryStepNameValues)
});
export const getDeviceInventoryProgressByTagContract = oc.route({
  method: 'GET',
  path: '/v2/boe/packages/IT_OPERATION_CORE/instance-tags/{tagId}/device-inventory-instances/progress'
}).input(GetDeviceInventoryProgressByTagInputSchema).output(GetDeviceInventoryProgressByTagOutputSchema);

// --- 4 / 5. セルフレポート結果一覧（束ね軸 / Instance 軸） ---

/**
 * @see BE `selfReportOrderFields`
 *
 * ⚠️ かつてあった `'STATUS'`（`DeviceOperationTask.progressStatus` が backing）は
 * BE から撤去された。v2 は `DeviceOperationTask` を読み書きしないため、工程の並び替えは
 * `'DISPLAY_STATUS'`（依頼 / 回答 / 情シス工程のタスク状態から解決する表示ステータス）が担う。
 */
const selfReportOrderFields = z.enum(['DEVICE_NAME', 'REPORTED_BY', 'REPORT_TYPE', 'REPORT_RESULT', 'DEVICE_CONDITION', 'REPORTED_AT', 'MEMO', 'DISPLAY_STATUS']);
export const DEVICE_INVENTORY_SELF_REPORT_ORDER_FIELDS = selfReportOrderFields.options;
export type DeviceInventorySelfReportOrderField = (typeof selfReportOrderFields.options)[number];

/**
 * 束ね画面のタブ。件数 API のレスポンスキーと 1:1 で、一覧 API の `tab` の値でもある
 * （`all` は「絞り込まない」= `tab` を送らないことで表すので含まれない）。
 *
 * @see BE `deviceInventoryTabValues` / `DEVICE_INVENTORY_TABS`
 */
const deviceInventoryTabValues = ['notRequested', 'notAnswered', 'noActionRequired', 'actionRequired', 'completed'] as const;
const DeviceInventoryTabSchema = z.enum(deviceInventoryTabValues);
export const DEVICE_INVENTORY_TABS = deviceInventoryTabValues;
const DeviceInventorySelfReportPersonSchema = z.object({
  id: z.string(),
  name: z.string(),
  avatarUrl: z.string().nullable()
});
const DeviceInventorySelfReportDeviceSchema = z.object({
  id: z.string(),
  name: z.string(),
  serialNumber: z.string().nullable(),
  model: z.string().nullable(),
  assetTag: z.string().nullable(),
  deviceTag: z.string().nullable(),
  genre: DeviceGenreSchema,
  avatarUrl: z.string().nullable()
});

/** セルフレポートの申告内容（FormData 由来） */
const DeviceInventorySelfReportDataSchema = z.object({
  deviceId: z.string(),
  reportResult: DeviceInventoryReportResultSchema,
  /** 「正当な所有（OWNED）」以外の申告では入らない */
  deviceCondition: z.enum(deviceInventoryConditionValues).optional(),
  reportedByPersonId: z.string(),
  memo: z.string().nullable(),
  reportedAt: z.string(),
  labelReadable: z.boolean(),
  deviceTag: z.string().nullable(),
  verificationMethod: z.enum(deviceInventoryVerificationMethodValues),
  reportedByPerson: DeviceInventorySelfReportPersonSchema.nullable()
});

/**
 * セルフレポート結果一覧の 1 行（束ね軸 / Instance 軸で共通）。
 *
 * 🔴 **`deviceTaskId` / `status`（`DeviceOperationTask.progressStatus`）は無い。**
 * v2 で束ねが `OperationPackageInstanceTag` へ移り、`DeviceOperationTask` が持つ情報は
 * すべて BOE 側の写しになったため、BE が wire から撤去した。復活させないこと。
 *
 * | 欲しかったもの | 代わりに読む値 |
 * | --- | --- |
 * | 行の識別子（`deviceTaskId`） | `instanceId`（1 デバイス = 1 OperationPackageInstance） |
 * | 依頼が済んでいるか | `displayStatus`（依頼 / 回答 / 情シス工程のタスク状態から解決） |
 * | 依頼タスクを完了できるか | instance 明細の依頼タスクの `status`（`deviceInventoryInstanceTasks.ts`） |
 */
const DeviceInventorySelfReportSchema = z.object({
  instanceId: z.string(),
  /**
   * 次に操作する対象のタスク。
   *
   * 回答が完了していれば「回答の後に情シスが行う工程」のタスク
   * （v1: `ReviewReport` / v2: `RespondToReportV2`）、未完了なら回答タスクを指す。
   * 🔴 **依頼タスクは絶対に入らない**（依頼は instance 明細から索引を引く）。
   */
  taskInstanceId: z.string(),
  anchorDate: z.string(),
  selfReport: DeviceInventorySelfReportDataSchema.nullable(),
  device: DeviceInventorySelfReportDeviceSchema,
  assignedPerson: DeviceInventorySelfReportPersonSchema.nullable(),
  /** UI表示用のステータス（タスク実行可否の制御） */
  displayStatus: DeviceInventoryDisplayStatusSchema,
  actionMemo: z.string().nullable(),
  /**
   * 対応が必要な報告か（束ね画面のタブ分類）
   *
   * - `false` / `true`: Step2（セルフレポート）完了後の分類結果
   * - `null`: **分類対象外**。Step2 が未完了（未報告 / 報告中）の行はここに入る。
   *   `?? false` で潰すと未回答が全部「対応不要」に落ちるので、
   *   タブ集計でも「対応不要」「要対応」のどちらにも数えないこと。
   */
  actionRequired: z.boolean().nullable()
});
const ListDeviceInventorySelfReportsByTagInputSchema = paginationQuerySchema(selfReportOrderFields).extend({
  tagId: z.string(),
  freeText: z.string().max(100).optional(),
  deviceGenres: z.array(DeviceGenreSchema).optional(),
  /**
   * 画面の「進行状況」フィルタ。一覧列のバッジと**同じ語彙**で絞り込む。
   *
   * 🔴 `progressStatus` で代用してはいけない。列に出しているのは `displayStatus` で
   * 値域が違うため、「列に見えている値で絞ったのに行が消える」ことになる。
   * 4 値への集約は FE 側
   * （`features/it-operation-core/device-inventory-progress-kind.ts`）で、
   * ここは BE と同じ 7 値を受ける。
   */
  displayStatuses: z.array(DeviceInventoryDisplayStatusSchema).optional(),
  /** 画面の「所持確認」フィルタ。未回答の行はどの値を指定しても返らない */
  reportResults: z.array(DeviceInventoryReportResultSchema).optional(),
  /**
   * 束ね画面のタブで絞り込む。省略は「すべて」。
   *
   * 件数 API と同じ分類器を BE 側で通すため、絞り込み（`freeText` / `deviceGenres` /
   * `displayStatuses` / `reportResults`）を掛けていない状態ではタブ件数と一覧の件数が
   * 一致する（掛けると一致しない。件数 API は `tagId` しか受けないため）。
   *
   * 🔴 `displayStatuses` で代用してはいけない。タブの境界は `displayStatus` の値域では
   * 表現できない。タブ分類は `FormData`（`reportResult` / `deviceCondition`）と
   * `OperationPackageInstance` の状態を組み合わせた業務判定で、`displayStatus`
   * （依頼 / 回答 / 対応の 3 工程のタスク状態から解決した値）にはその情報が入っていない。
   * 例: `displayStatus` が `REVIEW_COMPLETED` でも、その後に「セルフレポートへの対応」
   * タスクが開いて Instance が未完了のまま残るため `completed` タブには入らない
   * （BE の SQL spec の 0011 / 0012 が固定している形）。
   * `displayStatuses` は画面の「進行状況」フィルタで、タブとは独立して AND される。
   */
  tab: DeviceInventoryTabSchema.optional()
});
const ListDeviceInventorySelfReportsByTagOutputSchema = paginatedSchema(DeviceInventorySelfReportSchema);
export const listDeviceInventorySelfReportsByTagContract = oc.route({
  method: 'GET',
  path: '/v2/boe/packages/IT_OPERATION_CORE/instance-tags/{tagId}/device-inventory-self-reports'
}).input(ListDeviceInventorySelfReportsByTagInputSchema).output(ListDeviceInventorySelfReportsByTagOutputSchema);
export type ListDeviceInventorySelfReportsByTagInput = z.infer<typeof ListDeviceInventorySelfReportsByTagInputSchema>;
export type ListDeviceInventorySelfReportsByTagOutput = z.infer<typeof ListDeviceInventorySelfReportsByTagOutputSchema>;
export type DeviceInventorySelfReportOutput = z.infer<typeof DeviceInventorySelfReportSchema>;
const ListDeviceInventorySelfReportsByPackageInstancesInputSchema = paginationQuerySchema(selfReportOrderFields).extend({
  /** 明細を取得する OperationPackageInstance の ID */
  packageInstanceId: z.string(),
  freeText: z.string().max(100).optional(),
  deviceGenres: z.array(DeviceGenreSchema).optional(),
  /** 画面の「進行状況」フィルタ。一覧列のバッジと同じ語彙（`displayStatus`）で絞り込む */
  displayStatuses: z.array(DeviceInventoryDisplayStatusSchema).optional(),
  /** 画面の「所持確認」フィルタ。未回答の行はどの値を指定しても返らない */
  reportResults: z.array(DeviceInventoryReportResultSchema).optional()
});
export const listDeviceInventorySelfReportsByPackageInstancesContract = oc.route({
  method: 'GET',
  path: '/v2/boe/packages/IT_OPERATION_CORE/device-inventory-self-reports/by-package-instances'
}).input(ListDeviceInventorySelfReportsByPackageInstancesInputSchema).output(ListDeviceInventorySelfReportsByTagOutputSchema);
export type ListDeviceInventorySelfReportsByPackageInstancesInput = z.infer<typeof ListDeviceInventorySelfReportsByPackageInstancesInputSchema>;

/**
 * 一括完了できるデバイス棚卸しタスクの OperationTaskCode。
 *
 * BE 側も contract に**値を写している**（contract は `~/it-force/...` を import できないため）。
 * SSOT は BE の
 * `it-force/domain/service/device-inventory/device-inventory-bulk-complete-task-code.ts`。
 *
 * v1 パターン（`RequestSelfReport` / `ReviewReport`）と v2 パターン（`*V2`）で code が異なる。
 * 確認ノード（BOE 基盤の Confirmation）は撤去済みのため、v2 の回答後の工程も
 * 通常の `OperationTaskInstance`（`RespondToReportV2`）としてこの API で完了できる。
 */
const bulkCompletableTaskCodeValues = ['RequestSelfReport', 'RequestSelfReportV2', 'ReviewReport', 'RespondToReportV2'] as const;
export type DeviceInventoryBulkCompletableTaskCode = (typeof bulkCompletableTaskCodeValues)[number];

/** `taskInstanceIds` の上限。棚卸し起動の上限（BE の `MAX_DEVICE_COUNT`）に合わせている */
export const DEVICE_INVENTORY_BULK_COMPLETE_MAX_TASK_INSTANCES = 400;
const BulkCompleteDeviceInventoryTasksByTagInputSchema = z.object({
  /**
   * 束ねタグID
   *
   * **`requestId` は受けない。** 起動 1 回ごとに振られる `requestId` は
   * 「1 束ね = N requestId」（同名タグへの合流）を表現できず、束ね単位の一括処理では
   * 母集団を取り違える。requestId への分割は BE 側の関心事として閉じている。
   */
  tagId: z.string(),
  /**
   * 完了対象の OperationTaskInstance ID 群。
   *
   * 一覧 API（`device-inventory-self-reports`）の行の `taskInstanceId` をそのまま渡す。
   * どの Instance に属するかは BE が束ねタグの母集団から解決するため
   * `operationPackageInstanceId` は送らない。
   *
   * ⚠️ `taskInstanceId` は **回答の完了状況で指す先が変わる**
   * （v1: 完了済みなら ReviewReport タスク・未完了なら SubmitSelfReport タスク /
   * v2: 完了済みなら RespondToReportV2 タスク・未完了なら回答タスク）。
   * `taskCode: 'ReviewReport'` / `'RespondToReportV2'` で送れるのは
   * 回答完了済みの行（`actionRequired !== null`）だけ。
   *
   * ⚠️ **空配列は送れない**（`.min(1)`）。依頼タスク（`RequestSelfReport` 系）では
   * 「ここに含まれない Instance は束ね全体を母集団として Aborted」になるため、
   * 空配列を許すと全件中断になる。選択 0 件のときはボタンを disabled にすること。
   */
  taskInstanceIds: z.array(z.string()).min(1).max(DEVICE_INVENTORY_BULK_COMPLETE_MAX_TASK_INSTANCES),
  /** 完了させるタスクの OperationTaskCode（回答タスクはフォーム提出のため対象外） */
  taskCode: z.enum(bulkCompletableTaskCodeValues)
});
const BulkCompleteDeviceInventoryTasksByTagOutputSchema = z.object({
  /** 実行を試みたタスク数（= 重複除去後の taskInstanceIds の件数） */
  executedTaskCount: z.number(),
  /** 完了に成功したタスク数 */
  successTaskCount: z.number(),
  /** 完了に失敗したタスク数（部分失敗を許容するため 200 でも 0 より大きくなりうる） */
  failedTaskCount: z.number()
});
export const bulkCompleteDeviceInventoryTasksByTagContract = oc.route({
  method: 'POST',
  path: '/v2/boe/packages/IT_OPERATION_CORE/instance-tags/{tagId}/device-inventory-instances/tasks/bulk-complete'
}).input(BulkCompleteDeviceInventoryTasksByTagInputSchema).output(BulkCompleteDeviceInventoryTasksByTagOutputSchema);
export type BulkCompleteDeviceInventoryTasksByTagInput = z.infer<typeof BulkCompleteDeviceInventoryTasksByTagInputSchema>;
export type BulkCompleteDeviceInventoryTasksByTagOutput = z.infer<typeof BulkCompleteDeviceInventoryTasksByTagOutputSchema>;

// ============================================================================

/**
 * 束ね単位の一括アーカイブ（中断）。未回答のデバイスを棚卸し対象から外す。
 *
 * 🔴 **汎用の中断 API を直接叩いてはいけない。**
 * `POST /v2/boe/packages/{ownerProduct}/instances/{instanceId}/abort` が書くのは
 * `OperationPackageInstance.status` だけで、`DeviceOperationTask.progressStatus` は
 * `InProgress` のまま残る。棚卸しの読み取り（一覧 / タブ件数 / 進捗）は
 * **すべて `progressStatus` だけ**で中断済みを弾いているため、中断しても行が消えず
 * 未回答タブに残り続ける。この API は両方を 1 トランザクションで書く。
 *
 * 詳細は BE の `bulk-archive-device-inventory-instances-by-tag.api-contract.ts`。
 */
const BulkArchiveDeviceInventoryInstancesByTagInputSchema = z.object({
  /** 束ねタグID */
  tagId: z.string(),
  /**
   * 中断対象の `OperationPackageInstance` の id 群。
   *
   * 一覧 API の行の **`instanceId`** をそのまま渡す（`taskInstanceId` でも
   * `deviceTaskId` でもない）。中断はタスク単位ではなくデバイス 1 台ぶんの
   * 棚卸しインスタンス単位の操作。
   */
  operationPackageInstanceIds: z.array(z.string()).min(1).max(DEVICE_INVENTORY_BULK_COMPLETE_MAX_TASK_INSTANCES)
});
const BulkArchiveDeviceInventoryInstancesByTagOutputSchema = z.object({
  /** 実行を試みた件数（= 重複除去後の件数） */
  executedTaskCount: z.number(),
  /** 中断に成功した件数 */
  successTaskCount: z.number(),
  /** 中断できなかった件数（既に中断済み・完了済みなど。部分失敗を許容する） */
  failedTaskCount: z.number()
});
export const bulkArchiveDeviceInventoryInstancesByTagContract = oc.route({
  method: 'POST',
  path: '/v2/boe/packages/IT_OPERATION_CORE/instance-tags/{tagId}/device-inventory-instances/bulk-archive'
}).input(BulkArchiveDeviceInventoryInstancesByTagInputSchema).output(BulkArchiveDeviceInventoryInstancesByTagOutputSchema);
export type BulkArchiveDeviceInventoryInstancesByTagInput = z.infer<typeof BulkArchiveDeviceInventoryInstancesByTagInputSchema>;
export type BulkArchiveDeviceInventoryInstancesByTagOutput = z.infer<typeof BulkArchiveDeviceInventoryInstancesByTagOutputSchema>;

// ============================================================================
// パターン世代一覧（v1 / v2 の判定）
// ============================================================================

/**
 * デバイス棚卸しパターンの世代一覧。
 *
 * v1（旧・requestId 軸の 3 Step パターン）と v2（BOE 基盤の束ね画面パターン）が
 * 共存する間、パターン ID から世代を引いて画面をルーティングするために使う。
 *
 * 詳細は BE の
 * `list-device-inventory-pattern-generations.api-contract.ts`。
 */
const DeviceInventoryPatternGenerationSchema = z.enum(['v1', 'v2']);
const ListDeviceInventoryPatternGenerationsOutputSchema = z.object({
  items: z.array(z.object({
    patternId: z.string(),
    /**
     * パターン世代。v2 = BOE 基盤の 1 Step / 3 タスク構成（確認タスクなし）。
     * 判定は `RespondToReportV2` catalog の有無（名前・Step 数では判定しない）。
     */
    generation: DeviceInventoryPatternGenerationSchema
  }))
});
export const listDeviceInventoryPatternGenerationsContract = oc.route({
  method: 'GET',
  path: '/v2/boe/packages/IT_OPERATION_CORE/device-inventory/pattern-generations'
}).input(z.object({})).output(ListDeviceInventoryPatternGenerationsOutputSchema);
export type ListDeviceInventoryPatternGenerationsOutput = z.infer<typeof ListDeviceInventoryPatternGenerationsOutputSchema>;